import { RefreshCcw, Timer } from 'lucide-react';
import type { MouseEvent } from 'react';

type VersionReloadToastProps = {
  countdownSeconds: number | null;
  onNo: () => void;
  onReload: () => void;
  onActionMouseDown: (event: MouseEvent<HTMLButtonElement>, handler: () => void) => void;
  onActionClick: (event: MouseEvent<HTMLButtonElement>, handler: () => void) => void;
};

const formatHHMMSS = (seconds: number) => {
  const s = Math.max(0, Math.floor(seconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

export const VersionReloadToast = ({
  countdownSeconds,
  onNo,
  onReload,
  onActionMouseDown,
  onActionClick,
}: VersionReloadToastProps) => (
  <div className="pointer-events-auto w-full max-w-sm rounded-2xl border border-slate-200 bg-white shadow-xl">
    <div className="flex items-start gap-3 p-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100">
        {countdownSeconds === null ? (
          <RefreshCcw className="h-5 w-5 text-slate-700" />
        ) : (
          <Timer className="h-5 w-5 text-slate-700" />
        )}
      </div>
      <div>
        <p className="text-base font-semibold text-slate-900">
          {countdownSeconds === null ? 'Update available' : 'Reload scheduled'}
        </p>
        {countdownSeconds === null ? (
          <p className="text-sm text-slate-600">A new version of the app is ready. Reload to update.</p>
        ) : (
          <p className="text-sm text-slate-600">
            Will reload page in{' '}
            <span className="font-mono font-semibold text-slate-700">{formatHHMMSS(countdownSeconds)}</span>. Any
            unsaved changes will be lost.
          </p>
        )}
      </div>
    </div>
    <div className="flex justify-end gap-2 border-t border-slate-200 p-3">
      {countdownSeconds === null && (
        <button
          type="button"
          onMouseDown={(event) => onActionMouseDown(event, onNo)}
          onClick={(event) => onActionClick(event, onNo)}
          className="inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100"
        >
          Later
        </button>
      )}
      <button
        type="button"
        onMouseDown={(event) => onActionMouseDown(event, onReload)}
        onClick={(event) => onActionClick(event, onReload)}
        className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800"
      >
        Reload now
      </button>
    </div>
  </div>
);
